import { action, observable, reaction, runInAction, makeObservable } from 'mobx';
import { BaseListViewStore } from 'core/stores';
import { LookupStore } from 'modules/lookup/stores';
import { ModalParams, ConfirmModalParams } from 'core/models';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { translate, QueryUtility, isSome, makePlural, isSuperAdminOrganization } from 'core/utils';
import { localStorageHandler } from 'core/services';
import { orderBy } from '@progress/kendo-data-query';
import { checkPermissions } from 'core/stores';
import _ from 'lodash';
import validatorjs from 'validatorjs';
import XLSX from 'xlsx';

class LookupViewStore extends BaseListViewStore {
	@observable options = [];
	@observable selectedLookup = null;
	@observable initDone = false;
	@observable showActions = true;
	selectedItem = null;
	allList = {
		tableData: {
			columns: [
				{
					key: 'value',
					title: 'SELECTLOOKUP',
					width: false,
					resizable: false,
					sortable: true,
					selected: true,
				},
			],
			actions: {},
			sortObject: [
				{
					field: 'value',
					dir: 'asc',
				},
			],
		},
		queryUtility: new QueryUtility(this.rootStore),
	};

	editModal = new ModalParams({
		onClose: this.closeEditModal,
	});

	importModal = new ModalParams({
		onClose: this.closeEditModal,
	});

	deleteConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.deleteLookup(this.selectedItem);
		},
	});

	constructor(rootStore) {
		super(rootStore, {
			listStore: new LookupStore(rootStore),
			routes: {
				detail: id => this.rootStore.routerStore.goTo('master.main.lookup.preview', { id }),
				edit: id => this.rootStore.routerStore.setQueryParams({ editId: id }),
				create: () => this.rootStore.routerStore.setQueryParams({ create: null }),
				import: () => this.rootStore.routerStore.setQueryParams({ importData: true }),
			},
			permissions: {
				update: () => this.authStore.hasPermission('fulfillmentLookupSection.update'),
				create: () => this.authStore.hasPermission('fulfillmentLookupSection.create'),
				remove: () => this.authStore.hasPermission('fulfillmentLookupSection.delete'),
				read: () => this.authStore.hasPermission('fulfillmentLookupSection.read'),
				full: () => this.authStore.hasPermission('fulfillmentLookupSection.full'),
			},
			tableData: {
				columns: [
					{
						key: '',
						title: '',
						width: false,
						sortable: false,
						onClick: item => this.routes.edit(item.id),
					},
				],
				actions: {
					onEdit: async item => this.routes.edit(item.id),
					onDelete: async item => this.openDeleteConfirmModal(item),
				},
				sortObject: [],
			},
		});
		makeObservable(this);
		this.isSuperAdminOrganization = isSuperAdminOrganization(this.rootStore);
		if (this.isSuperAdminOrganization) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findLookup);
		this.showActions = checkPermissions(this).actionsAllowed;
		runInAction(() => {
			// Filter persistance (load)
			let filter = localStorageHandler.get('lookup-filter');
			if (filter) {
				this.selectedLookup = filter;
			}
			if (this.rootStore.routerStore.routerState.queryParams.create == null) {
				this.rootStore.routerStore.silentTransition('master.app.main.lookup.list', { page: 1, rpp: 50 });
			}
		});
		this.initialize();
		this.setHeaderModes();

		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.lookup.list') {
					return;
				}
				const { editId, create, importData } = state.queryParams;

				if (create !== undefined) {
					state.queryParams.page = undefined;
					state.queryParams.rpp = undefined;
				}

				var editOpen = editId !== undefined || create !== undefined;
				var importOpen = !editOpen && importData !== undefined;
				this.editModal.setOpen(editOpen);
				this.importModal.setOpen(importOpen);
			}
		);
	}

	@action.bound async initialize() {
		var Validator = validatorjs;
		Validator.register(
			'numericMinValue',
			value => {
				return 1 / value > 0;
			},
			'NUMERICMINVALUE'
		);
		Validator.register(
			'invalidCharacter',
			value => {
				return value[value.length - 1] !== '.' && !value.toString().includes('e');
			},
			'INVALIDCHARACTER'
		);
		this.queryUtility.filter.orderBy = this.allList.tableData.sortObject[0].field;
		this.queryUtility.filter.orderDirection = this.allList.tableData.sortObject[0].dir;
		this.lookups = [
			{
				lookup: lookupRouteName.cancelReason,
				columnName: 'LOOKUP.CANCELREASON',
				fields: {
					name: {
						label: 'LOOKUP.CANCELREASON',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.confirmationType,
				columnName: 'LOOKUP.CONFIRMATIONTYPE',
				fields: {
					name: {
						label: 'LOOKUP.CONFIRMATIONTYPE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.carrierType,
				columnName: 'LOOKUP.CARRIERTYPE',
				fields: {
					name: {
						label: 'LOOKUP.CARRIERTYPE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.closeOutType,
				columnName: 'LOOKUP.CLOSEOUTTYPE',
				fields: {
					name: {
						label: 'LOOKUP.CLOSEOUTTYPE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.currency,
				columnName: 'LOOKUP.CURRENCY',
				fields: {
					currency: {
						label: 'LOOKUP.CURRENCY',
						rules: 'required|string',
					},
					code: {
						label: 'LOOKUP.CODE',
						rules: 'required|string',
					},
					description: {
						label: 'LOOKUP.DESCRIPTION',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.warehouseActivityType,
				columnName: 'LOOKUP.WAREHOUSEACTIVITYTYPE',
				fields: {
					name: {
						label: 'LOOKUP.WAREHOUSEACTIVITYTYPE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.receivingStatus,
				columnName: 'LOOKUP.RECEIVINGSTATUS',
				fields: {
					name: {
						label: 'LOOKUP.RECEIVINGSTATUS',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.receivingType,
				columnName: 'LOOKUP.RECEIVINGTYPE',
				fields: {
					name: {
						label: 'LOOKUP.RECEIVINGTYPE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.channelType,
				columnName: 'LOOKUP.CHANNELTYPE',
			},
			{
				lookup: lookupRouteName.dimensionUnit,
				columnName: 'LOOKUP.DIMENSIONUNIT',
				fields: {
					name: {
						label: 'LOOKUP.DIMENSIONUNIT',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.shipmentInfoStatus,
				columnName: 'LOOKUP.SHIPMENTINFOSTATUS',
				fields: {
					name: {
						label: 'LOOKUP.SHIPMENTINFOSTATUS',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.orderStatus,
				columnName: 'LOOKUP.ORDERSTATUS',
				fields: {
					name: {
						label: 'LOOKUP.ORDERSTATUS',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.tagType,
				columnName: 'LOOKUP.TAGTYPE',
				fields: {
					name: {
						label: 'LOOKUP.TAGTYPE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.onHoldReason,
				columnName: 'LOOKUP.ONHOLDREASON',
				fields: {
					name: {
						label: 'LOOKUP.ONHOLDREASON',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.returnReason,
				columnName: 'LOOKUP.RETURNREASON',
				fields: {
					name: {
						label: 'LOOKUP.RETURNREASON',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.replacementReason,
				columnName: 'LOOKUP.REPLACEMENTREASON',
				fields: {
					name: {
						label: 'LOOKUP.REPLACEMENTREASON',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.productCategory,
				columnName: 'LOOKUP.PRODUCTCATEGORY',
				fields: {
					name: {
						label: 'LOOKUP.PRODUCTCATEGORY',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.orderType,
				columnName: 'LOOKUP.ORDERTYPE',
				fields: {
					name: {
						label: 'LOOKUP.ORDERTYPE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.condition,
				columnName: 'LOOKUP.CONDITION',
				fields: {
					name: {
						label: 'LOOKUP.CONDITION',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.orderLineStatus,
				columnName: 'LOOKUP.ORDERLINESTATUS',
				fields: {
					name: {
						label: 'LOOKUP.ORDERLINESTATUS',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.productStatus,
				columnName: 'LOOKUP.PRODUCTSTATUS',
				fields: {
					name: {
						label: 'LOOKUP.PRODUCTSTATUS',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.tag,
				embed: ['tagType'],
				dropDownValueModel: { tagType: ['tagTypeId', 'id', 'name'] },
				columnName: 'LOOKUP.TAG',
				fields: {
					name: {
						label: 'LOOKUP.TAG',
						rules: 'required|string',
					},
					tagTypeId: {
						label: 'LOOKUP.TAGTYPE',
						placeholder: 'LOOKUP.TAGTYPECHOOSE',
						rules: 'required',
						extra: {
							type: 'Lookup',
							searchBy: 'name',
							service: {
								find: async filter => {
									return await this.listStore.getLookupService(lookupRouteName.tagType).find(filter);
								},
							},
						},
					},
				},
			},
			{
				lookup: lookupRouteName.timezone,
				columnName: 'LOOKUP.TIMEZONE',
				fields: {
					name: {
						label: 'LOOKUP.TIMEZONE',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.weightUnit,
				columnName: 'LOOKUP.WEIGHTUNIT',
				fields: {
					name: {
						label: 'LOOKUP.WEIGHTUNIT',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.warehouseStatus,
				columnName: 'LOOKUP.WAREHOUSESTATUS',
				fields: {
					name: {
						label: 'LOOKUP.WAREHOUSESTATUS',
						rules: 'required|string',
					},
				},
			},
			{
				lookup: lookupRouteName.box,
				columnName: 'LOOKUP.BOXLOOKUP',
				fields: {
					name: {
						label: 'LOOKUP.BOX.NAME',
						rules: 'required|string',
					},
					boxLength: {
						label: 'LOOKUP.BOX.LENGTH',
						rules: 'required|numeric|numericMinValue|invalidCharacter',
					},
					boxWidth: {
						label: 'LOOKUP.BOX.WIDTH',
						rules: 'required|numeric|numericMinValue|invalidCharacter',
					},
					boxHeight: {
						label: 'LOOKUP.BOX.HEIGHT',
						rules: 'required|numeric|numericMinValue|invalidCharacter',
					},
				},
			},
			{
				lookup: lookupRouteName.carrierServiceType,
				columnName: 'LOOKUP.CARRIERSERVICETYPE',
				fields: {
					name: {
						label: 'LOOKUP.CARRIERSERVICETYPE',
						rules: 'required|string',
					},
				},
			},
		];
		if (this.isSuperAdminOrganization) {
			this.lookups = _.concat(this.lookups, [
				{
					lookup: lookupRouteName.carrierDeliveryStatus,
					embed: ['carrierType', 'shipmentInfoDeliveryStatus'],
					dropDownValueModel: {
						carrierType: ['carrierTypeId', 'id', 'name'],
						shipmentInfoDeliveryStatus: ['shipmentInfoDeliveryStatusId', 'id', 'name'],
					},
					columnName: 'LOOKUP.CARRIERDELIVERYSTATUS',
					fields: {
						name: {
							label: 'LOOKUP.CARRIERDELIVERYSTATUS',
							rules: 'required|string',
						},
						carrierTypeId: {
							label: 'LOOKUP.CARRIERTYPE',
							rules: 'required',
							extra: {
								type: 'Lookup',
								searchBy: 'name',
								service: {
									find: async filter => {
										return await this.listStore.getLookupService(lookupRouteName.carrierType).find(filter);
									},
								},
							},
						},
						shipmentInfoDeliveryStatusId: {
							label: 'LOOKUP.SHIPMENTINFODELIVERYSTATUS',
							rules: 'required',
							extra: {
								type: 'Lookup',
								searchBy: 'name',
								service: {
									find: async filter => {
										return await this.listStore
											.getLookupService(lookupRouteName.shipmentInfoDeliveryStatus)
											.find(filter);
									},
								},
							},
						},
					},
				},
				{
					lookup: lookupRouteName.shipmentInfoDeliveryStatus,
					columnName: 'LOOKUP.SHIPMENTINFODELIVERYSTATUS',
					fields: {
						name: {
							label: 'LOOKUP.SHIPMENTINFODELIVERYSTATUS',
							rules: 'required|string',
						},
					},
				},
			]);
		}

		// Do early translate for itemName
		_.each(this.lookups, (val, key, col) => {
			if (!_.has(col[key], 'itemName')) {
				col[key].itemName = translate(val.columnName).toLowerCase();
			}
			if (!_.has(col[key], 'columnKey')) {
				col[key].columnKey = 'name';
			}
		});
		this.options = _.map(this.lookups, function(item) {
			if (item) {
				return { value: translate(item.columnName), label: translate(item.columnName), parameters: item };
			}
		});
		if (this.selectedLookup != null) {
			// Rebind (if coming from filter/local-storage for example)
			let parameters = _.find(this.lookups, l => l.columnName == this.selectedLookup.parameters.columnName);
			this.selectedLookup.parameters = parameters;
			await this.onLookupSelect({ dataItem: this.selectedLookup });
		} else {
			this.allList.queryUtility.initialize(() => {
				this.listStore.response.item = orderBy(
					_.map(this.lookups, item => {
						return {
							value: translate(item.columnName),
							parameters: item,
							label: translate(item.columnName),
						};
					}),
					this.allList.tableData.sortObject
				);
			}, false);
		}
		this.initDone = true;
	}

	@action.bound
	onAllLookupSort = () => {
		if (this.allList.tableData.sortObject[0].dir === 'asc') {
			this.allList.tableData.sortObject[0].dir = 'desc';
		} else {
			this.allList.tableData.sortObject[0].dir = 'asc';
		}
		this.queryUtility.filter.orderBy = this.allList.tableData.sortObject[0].field;
		this.queryUtility.filter.orderDirection = this.allList.tableData.sortObject[0].dir;
		this.listStore.response.item = orderBy(
			_.map(this.lookups, item => {
				return {
					value: translate(item.columnName),
					parameters: item,
					label: translate(item.columnName),
				};
			}),
			this.allList.tableData.sortObject
		);
	};

	@action.bound async onManagerSync(evt) {
		evt.preventDefault(true);
		this.suspend();
		try {
			await this.listStore.createLookup({}, lookupRouteName.managerSync, sourceOfChange.createLookup);
		} catch ({ statusCode, data }) {
			if (data && data.message) {
				this.rootStore.notificationStore.error(data.message);
			} else {
				this.rootStore.notificationStore.error([
					'LOOKUPSYNCERROR',
					{ name: this.selectedLookup.parameters.itemName, code: statusCode },
				]);
			}
		}
		this.resume();
		this.queryUtility.initialize(false);
	}

	@action.bound async onLookupSelect(eventProps) {
		const readOnly = ['channel-types', 'order-types'];
		const item = eventProps ? eventProps.dataItem || eventProps : null;
		this.selectedLookup = item;
		if (item) {
			if (_.indexOf(readOnly, item.parameters.lookup) > -1) {
				this.showActions = false;
			} else {
				this.showActions = true;
			}

			// Init table view column
			if (item.label === 'Currency') {
				this.tableData.columns[0].key = 'currency';
				this.tableData.columns[0].title = item.parameters.columnName;
				this.tableData.columns[0].width = 200;
				if (!_.find(this.tableData.columns, { key: 'code' })) {
					this.tableData.columns.push({ key: 'code', title: 'LOOKUP.CODE', width: 200 });
				}
			} else {
				if (_.find(this.tableData.columns, { key: 'code' })) {
					_.remove(this.tableData.columns, { key: 'code' });
				}
				this.tableData.columns[0].key = item.parameters.columnKey;
				this.tableData.columns[0].title = item.parameters.columnName;
			}
			// Init edit store item name (create and edit)
			this.editView = {};
			this.editView.itemName = item.parameters.itemName;
			// Extra fields are added on the top of the standard lookup (standard is 'name' only)
			var initFields = [];
			if (isSome(item.parameters.fields)) {
				_.forEach(item.parameters.fields, (val, key) => {
					initFields.push(Object.assign({ name: key, value: '' }, val));
				});
			}
			this.editView.initFields = initFields;
			// Init buttons
			let buttons = [];
			let itemName = translate(item.parameters.itemName);
			let itemNames = makePlural(itemName);
			let { extraButton } = item.parameters;

			if (this.permissions.create() && _.indexOf(readOnly, item.parameters.lookup) < 0) {
				buttons = [
					{
						className: 'btn btn--dropdown header__button',
						iconClassName: 'fa fa-plus spc--right--sml',
						onClick: this.routes.create,
						text: translate('NEW') + ' ' + _.startCase(itemName),
					},
					{
						className: 'btn btn--dropdown header__button',
						iconClassName: 'fa fa-file-import spc--right--sml',
						onClick: this.routes.import,
						text: translate('IMPORT') + ' ' + _.startCase(itemNames),
					},
				];
				if (extraButton) {
					buttons.push({
						className: 'btn btn--dropdown header__button',
						iconClassName: extraButton.iconClassName,
						onClick: extraButton.onClick,
						text: translate(extraButton.text),
					});
				}
			}
			this.setButtonComponent({ buttons: buttons });
			this.setSearchVisible();
		} else {
			this.setButtonComponent();
			this.setSearchVisible(false);
			this.initialize();
		}
		this.queryUtility.initialize(false);
		await this.queryUtility.changePage(1);
		// Filter persistance (save)
		localStorageHandler.set('lookup-filter', this.selectedLookup);
	}

	@action.bound closeEditModal() {
		this.rootStore.routerStore.updateQueryParams({
			editId: undefined,
			create: undefined,
			importData: undefined,
			...this.queryUtility.getFilterUrlParams(),
		});
	}

	@action.bound async openDeleteConfirmModal(item) {
		this.selectedItem = item;
		this.deleteConfirmModal.open({
			message: [
				'LOOKUPDELETECONFIRM.LOOKUP',
				{ name: item.name ? item.name : item[Object.keys(item)[1]], lookup: this.selectedLookup.parameters.itemName },
			],
		});
	}

	@action.bound async onFilesDrop(filesMeta) {
		this.closeEditModal();
		if (filesMeta && filesMeta.length == 1) {
			let items = [];
			let fileMeta = filesMeta[0];
			let reader = new FileReader();
			let xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
			let xls = 'application/vnd.ms-excel';
			if (
				fileMeta.type === xlsx ||
				(fileMeta.type === xls && !fileMeta.name.includes('csv', fileMeta.name.length - 3))
			) {
				// Excel processing (excluding csv)
				reader.onload = async evt => {
					const workbook = XLSX.read(evt.target.result, { type: 'binary' });
					const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
					let data = XLSX.utils.sheet_to_json(firstWorksheet, { header: 1 });
					_.forEach(data, dataItems => {
						_.forEach(dataItems, item => {
							item.length ? items.push({ abrv: encodeURIComponent(item).toLowerCase(), name: item }) : false;
						});
					});
					await this.createLookups(items);
				};
				reader.readAsBinaryString(fileMeta);
			} else {
				reader.onload = async evt => {
					let data = evt.target.result.split(/\r?\n/);
					_.forEach(data, dataItems => {
						dataItems = dataItems.split(',').map(item => item.trim());
						_.forEach(dataItems, item => {
							item.length ? items.push({ abrv: encodeURIComponent(item).toLowerCase(), name: item }) : false;
						});
					});
					await this.createLookups(items);
				};
				reader.readAsText(fileMeta, 'CP1252');
			}
		} else if (filesMeta && filesMeta.length == 0) {
			this.rootStore.notificationStore.error([
				'LOOKUPIMPORTERROR',
				{ lookup: this.selectedLookup.parameters.itemName, code: 'LOOKUP.WRONGFILEFORMAT' },
			]);
		} else {
			this.rootStore.notificationStore.error([
				'LOOKUPIMPORTERROR',
				{ lookup: this.selectedLookup.parameters.itemName, code: 'LOOKUP.MULTIPLEFILES' },
			]);
		}
	}

	@action.bound async createLookups(items) {
		try {
			await this.listStore.createLookups(items, this.selectedLookup.parameters.lookup, sourceOfChange.createLookup);
			this.rootStore.notificationStore.success(['LOOKUPIMPORT', { lookup: this.selectedLookup.parameters.itemName }]);
			await this.findLookup();
		} catch ({ statusCode, data }) {
			if (data && data.message) {
				if (statusCode === 409) {
					this.rootStore.notificationStore.warning([
						'LOOKUP.CONFLICT',
						{ lookup: this.selectedLookup.parameters.itemName },
					]);
				} else {
					this.rootStore.notificationStore.warning(t(data.message));
				}
			} else {
				this.rootStore.notificationStore.error([
					'LOOKUPIMPORTERROR',
					{ lookup: this.selectedLookup.parameters.itemName, code: statusCode },
				]);
			}
		}
	}

	@action.bound async findLookup() {
		this.suspend();
		var response = {};
		let filter = this.queryUtility.filter;
		if (this.selectedLookup) {
			if (this.selectedLookup.parameters.embed) {
				filter.embed = this.selectedLookup.parameters.embed;
			}
			await this.listStore.find(filter, this.selectedLookup.parameters.lookup);
			if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
				await this.queryUtility.changePage(1);
			}
			response = this.listStore.response;
			this.queryUtility.handleResponse(response);
		}
		this.resume();
	}

	@action async deleteLookup(item) {
		this.suspend();
		try {
			await this.listStore.deleteLookup({ id: item.id }, this.selectedLookup.parameters.lookup);
			this.rootStore.notificationStore.success([
				'LOOKUPDELETE.LOOKUP',
				{ name: item.name ? item.name : item[Object.keys(item)[1]], lookup: this.selectedLookup.parameters.itemName },
			]);
			await this.findLookup();
		} catch ({ statusCode, data }) {
			if (statusCode === 400) {
				this.rootStore.notificationStore.error([
					'LOOKUPDELETEERROR.LOOKUP',
					{
						lookup: this.selectedLookup.parameters.itemName,
						code: 'LOOKUPREFERENCEERROR',
					},
				]);
			} else {
				if (data && data.message) {
					this.rootStore.notificationStore.error(data.message);
				} else {
					this.rootStore.notificationStore.error([
						'LOOKUPDELETEERROR.LOOKUP',
						{ lookup: this.selectedLookup.parameters.itemName, code: statusCode },
					]);
				}
			}
		}
		this.resume();
	}
}

export default LookupViewStore;



// WEBPACK FOOTER //
// ./src/modules/lookup/stores/LookupViewStore.js